import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import axios from 'axios';
import {
  Inbox, MapPin, Clock, Zap, Lock, Unlock, Loader2, AlertCircle, CreditCard, Award, TrendingUp, Mail, Phone, CheckCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const urgencyColors = {
  low: 'bg-gray-100 text-gray-600',
  normal: 'bg-blue-50 text-blue-600',
  urgent: 'bg-orange-50 text-orange-600',
  emergency: 'bg-red-50 text-red-600',
};

const qualityColors = {
  purple: 'bg-purple-50 text-purple-700 border-purple-200',
  green: 'bg-green-50 text-green-700 border-green-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  gray: 'bg-gray-50 text-gray-700 border-gray-200'
};

// Helper function to get relative time
const getRelativeTime = (isoString) => {
  if (!isoString) return '';
  
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  return date.toLocaleDateString();
};

// Lead freshness colour buckets
// <1h  → green  (fresh, high conversion)
// 1–6h → yellow (still warm)
// 6–24h → amber (cooling)
// >24h → gray  (stale)
const getFreshnessClass = (isoString) => {
  if (!isoString) return 'bg-gray-50 text-gray-500 border-gray-200';
  const diffHours = (Date.now() - new Date(isoString).getTime()) / 3600000;
  if (diffHours < 1) return 'bg-green-50 text-green-700 border-green-200';
  if (diffHours < 6) return 'bg-yellow-50 text-yellow-700 border-yellow-200';
  if (diffHours < 24) return 'bg-amber-50 text-amber-700 border-amber-200';
  return 'bg-gray-100 text-gray-500 border-gray-200';
};

// "Hot lead" — posted within the last 30 minutes. Triggers urgency cue on the badge.
const isHotLead = (isoString) => {
  if (!isoString) return false;
  const diffMin = (Date.now() - new Date(isoString).getTime()) / 60000;
  return diffMin >= 0 && diffMin < 30;
};

const AvailableLeads = () => {
  const { pro, proId } = useOutletContext();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(null);
  const [error, setError] = useState('');

  const fetchLeads = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/available-leads/${proId}`);
      setLeads(res.data);
    } catch (e) {
      console.error('Error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    fetchLeads(); 
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proId]);

  const handleUnlock = async (lead) => {
    if (!pro?.is_active_professional) {
      setError('Your account is pending verification. Please contact support to activate lead unlocking.');
      return;
    }
    if (!pro?.is_verified) {
      setError("Your identity verification is still pending admin review. You'll be able to unlock leads once it's approved.");
      return;
    }
    const totalBalance = (pro?.free_credits_balance || 0) + (pro?.paid_credits_balance || 0);
    if (totalBalance < lead.unlock_cost) {
      setError('Insufficient credits. Please top up your wallet.');
      return;
    }
    setUnlocking(lead.id);
    setError('');
    try {
      await axios.post(`${API}/leads/unlock`, {
        professional_id: proId,
        service_request_id: lead.id,
        credits_used: lead.unlock_cost
      });
      // Refresh leads
      await fetchLeads();
      // Update pro balance locally (approximate - actual balance calculated server-side)
      if (pro) {
        const newFreeBalance = Math.max(0, (pro.free_credits_balance || 0) - lead.unlock_cost);
        const deductedFromFree = (pro.free_credits_balance || 0) - newFreeBalance;
        const deductedFromPaid = lead.unlock_cost - deductedFromFree;
        pro.free_credits_balance = newFreeBalance;
        pro.paid_credits_balance = Math.max(0, (pro.paid_credits_balance || 0) - deductedFromPaid);
      }
    } catch (e) {
      if (e.response?.status === 402) setError('Insufficient credits.');
      else if (e.response?.status === 409) setError('Lead already unlocked.');
      else if (e.response?.status === 403) setError(e.response?.data?.detail || 'Your account is pending verification.');
      else setError('Failed to unlock lead.');
    } finally {
      setUnlocking(null);
    }
  };

  const availableLeads = leads.filter(l => !l.is_unlocked);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={32} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Available Leads</h1>
        <p className="text-sm text-gray-500 mt-1">
          Service requests matching your expertise. Unlock to view contact details.
        </p>
      </div>

      {pro && !pro.is_active_professional && (
        <div
          data-testid="inactive-pro-banner"
          className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-xl"
        >
          <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Your account is pending verification</p>
            <p className="text-amber-700 mt-0.5">
              Lead unlocking is currently disabled. Please contact support to activate your account.
            </p>
          </div>
        </div>
      )}

      {pro && pro.is_active_professional && !pro.is_verified && (
        <div
          data-testid="unverified-pro-banner"
          className="mb-4 flex items-start gap-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm px-4 py-3 rounded-xl"
        >
          <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Identity verification pending</p>
            <p className="text-amber-700 mt-0.5">
              Lead unlocking turns on automatically once your ID document and selfie are approved.
            </p>
          </div>
        </div>
      )}

      {error && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 text-red-700 text-sm px-4 py-3 rounded-xl">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {availableLeads.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
          <Inbox size={40} className="text-gray-300 mx-auto mb-4" />
          <h3 className="text-base font-semibold text-gray-900 mb-1">No new leads right now</h3>
          <p className="text-sm text-gray-500">Check back soon — new requests come in regularly.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {availableLeads.map((lead) => {
            // Truncate description to 120 characters
            const shortDescription = lead.description 
              ? (lead.description.length > 120 
                  ? lead.description.substring(0, 120) + '...' 
                  : lead.description)
              : '';

            return (
              <div key={lead.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden hover:shadow-sm transition-shadow">
                <div className="p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Header with Service Type, Urgency, Quality Badge, and Capacity */}
                      <div className="flex items-center gap-2 flex-wrap mb-3">
                        <h3 className="text-lg font-bold text-gray-900">{lead.service_name}</h3>
                        
                        {/* Capacity Badge */}
                        {lead.is_at_capacity && (
                          <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
                            CLOSED ({lead.unlock_count}/{lead.max_professionals})
                          </span>
                        )}
                        
                        {/* Quality Score Badge */}
                        {lead.quality_badge && !lead.is_at_capacity && (
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border flex items-center gap-1 ${qualityColors[lead.quality_badge.color] || qualityColors.gray}`}>
                            {lead.quality_score >= 80 && <Award size={12} />}
                            {lead.quality_score >= 60 && lead.quality_score < 80 && <TrendingUp size={12} />}
                            {lead.quality_badge.label}
                          </span>
                        )}
                        
                        {/* Urgency Badge */}
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${urgencyColors[lead.urgency_level] || urgencyColors.normal}`}>
                          {lead.urgency_level || 'normal'}
                        </span>
                        
                        {/* Unlock Count (if not at capacity) */}
                        {!lead.is_at_capacity && lead.unlock_count > 0 && (
                          <span className="text-xs text-gray-500">
                            {lead.unlock_count}/{lead.max_professionals} unlocked
                          </span>
                        )}
                      </div>

                      {/* Verification Badges */}
                      {(lead.email_verified || lead.phone_verified) && (
                        <div className="flex items-center gap-2 mb-3">
                          {lead.email_verified && (
                            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 flex items-center gap-1">
                              <Mail size={11} />
                              Email Verified
                            </span>
                          )}
                          {lead.phone_verified && (
                            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 flex items-center gap-1">
                              <Phone size={11} />
                              Phone Verified
                            </span>
                          )}
                        </div>
                      )}

                      {/* Scarcity Indicators */}
                      {!lead.is_unlocked && !lead.is_at_capacity && (
                        <div className="mb-3">
                          {lead.unlock_count === 3 && (
                            <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg">
                              <AlertCircle size={16} className="text-orange-600" />
                              <div>
                                <p className="text-sm font-bold text-orange-700">Last Spot Available!</p>
                                <p className="text-xs text-orange-600">Only 1 professional can unlock this lead</p>
                              </div>
                            </div>
                          )}
                          {lead.unlock_count > 0 && lead.unlock_count < 3 && (
                            <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                              <p className="text-sm font-semibold text-amber-700">
                                {lead.unlock_count} of {lead.max_professionals} professionals have unlocked this lead
                              </p>
                              <p className="text-xs text-amber-600 mt-0.5">
                                {lead.max_professionals - lead.unlock_count} spot{lead.max_professionals - lead.unlock_count > 1 ? 's' : ''} remaining
                              </p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Short Description Preview (Max 120 chars) */}
                      {shortDescription && (
                        <p className="text-sm text-gray-600 mb-3 leading-relaxed">{shortDescription}</p>
                      )}

                      {/* Key Details: Location, Budget, Posted Time */}
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600 mb-3">
                        {lead.location && (
                          <span className="flex items-center gap-1.5">
                            <MapPin size={14} className="text-gray-400" />
                            <span className="font-medium">{lead.location}</span>
                          </span>
                        )}
                        {lead.budget_range && (
                          <span className="flex items-center gap-1.5">
                            <CreditCard size={14} className="text-gray-400" />
                            <span className="font-medium">{lead.budget_range}</span>
                          </span>
                        )}
                        {lead.created_at && (
                          <span
                            data-testid={`freshness-badge-${lead.id}`}
                            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold border ${getFreshnessClass(lead.created_at)}`}
                          >
                            {isHotLead(lead.created_at) ? (
                              <span data-testid={`hot-lead-tag-${lead.id}`} className="font-bold">
                                🔥 Hot lead ·
                              </span>
                            ) : (
                              <Clock size={11} />
                            )}
                            Posted {getRelativeTime(lead.created_at)}
                          </span>
                        )}
                      </div>

                      {/* Contact Details - LOCKED */}
                      <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                        <Lock size={14} className="text-amber-500" />
                        <span className="text-xs font-medium text-gray-600">
                          Contact details hidden • Unlock to view customer information
                        </span>
                      </div>
                    </div>

                    {/* Unlock Button with Dynamic Pricing */}
                    <div className="flex-shrink-0 text-center">
                      {lead.is_at_capacity ? (
                        <div className="text-center">
                          <div className="bg-gray-100 text-gray-500 font-semibold px-5 py-2.5 rounded-lg text-sm flex items-center gap-2 cursor-not-allowed">
                            <Lock size={14} />
                            Lead Closed
                          </div>
                          <p className="text-xs text-gray-500 mt-2">
                            Capacity reached
                          </p>
                        </div>
                      ) : (
                        <>
                          <button
                            data-testid={`unlock-lead-btn-${lead.id}`}
                            onClick={() => handleUnlock(lead)}
                            disabled={unlocking === lead.id || !pro?.is_active_professional || !pro?.is_verified}
                            title={!pro?.is_active_professional || !pro?.is_verified ? 'Account pending verification' : ''}
                            className="bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold px-5 py-2.5 rounded-lg transition-colors flex items-center gap-2 text-sm whitespace-nowrap"
                          >
                            {unlocking === lead.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : !pro?.is_active_professional || !pro?.is_verified ? (
                              <Lock size={14} />
                            ) : (
                              <Unlock size={14} />
                            )}
                            {!pro?.is_active_professional || !pro?.is_verified ? 'Locked' : 'Unlock Lead'}
                          </button>
                          <div className="mt-2">
                            <p className="text-xs text-amber-600 flex items-center justify-center gap-1 font-semibold">
                              <Zap size={12} className="text-amber-500" />
                              {lead.unlock_cost} credits
                            </p>
                            {lead.monetary_value && (
                              <p className="text-xs text-gray-600 mt-0.5">
                                R{lead.monetary_value}
                              </p>
                            )}
                            {lead.category_name && (
                              <p className="text-xs text-gray-500 mt-1">
                                {lead.category_name}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AvailableLeads;
